import { Alert, Button, Spin } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import CatchBoardBoard from './CatchBoardBoard'
import CatchBoardDealModal from './CatchBoardDealModal'
import CatchBoardEmptyState from './CatchBoardEmptyState'
import CatchBoardFilters from './CatchBoardFilters'
import CatchBoardMomentumStrip from './CatchBoardMomentumStrip'
import CatchBoardTipLabel from './CatchBoardTipLabel'
import {
  CATCH_BOARD_DEFAULT_READINESS_FILTER,
  CATCH_BOARD_OWNER_FILTER,
} from './catchBoardConstants'
import { getCatchBoardEmptyCopy } from 'src/utils/catchBoardEmptyCopy'
import { CATCH_BOARD_TOOLTIPS } from 'src/utils/catchBoardTooltips'
import {
  loadCatchBoardFilterPrefs,
  mergeCatchBoardFilterPrefs,
  saveCatchBoardFilterPrefs,
} from 'src/utils/catchBoardFilterPrefs'
import {
  DEFAULT_CATCH_BOARD_FILTERS,
  useCatchBoardData,
} from './useCatchBoardData'
import { useCatchBoardDragDrop } from './useCatchBoardDragDrop'
import {
  focusCatchBoardSearch,
  getCatchBoardShortcut,
} from 'src/utils/catchBoardShortcuts'

const CLOSED_DEAL_MODAL = {
  open: false,
  dealId: null,
  ownerNameHint: '',
  primaryVenueNameHint: '',
  createDraft: null,
}

/**
 * Catch Board panel — sales kanban on The Bridge.
 */
const CatchBoard = ({
  createHandoff = null,
  onCreateHandoffConsumed,
  isActive = true,
  deepLinkDealId = null,
  onDeepLinkDealChange,
} = {}) => {
  const account = useSelector(state => state.auth?.account)
  const accountId = account?._id || null

  const [filters, setFilters] = useState(DEFAULT_CATCH_BOARD_FILTERS)
  const hydratedAccountIdRef = useRef(null)

  const applyStickyFilters = useCallback(
    updater => {
      setFilters(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        if (accountId) {
          saveCatchBoardFilterPrefs(accountId, next)
        }
        return next
      })
    },
    [accountId],
  )

  useEffect(() => {
    if (!accountId) {
      return undefined
    }
    if (hydratedAccountIdRef.current === String(accountId)) {
      return undefined
    }
    hydratedAccountIdRef.current = String(accountId)

    const prefs = loadCatchBoardFilterPrefs(accountId)
    if (prefs) {
      setFilters(prev =>
        mergeCatchBoardFilterPrefs(
          {
            ...DEFAULT_CATCH_BOARD_FILTERS,
            search: prev.search,
          },
          prefs,
        ),
      )
    }
    return undefined
  }, [accountId])

  const { board, setBoard, loading, error, refresh } = useCatchBoardData(filters)
  const {
    sensors,
    activeDeal,
    isPersisting,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = useCatchBoardDragDrop({ board, setBoard })

  const [dealModal, setDealModal] = useState(CLOSED_DEAL_MODAL)
  const wasActiveRef = useRef(isActive)

  const closeDealModal = useCallback(
    ({ syncUrl = true } = {}) => {
      setDealModal({ ...CLOSED_DEAL_MODAL })
      if (syncUrl) {
        onDeepLinkDealChange?.(null)
      }
    },
    [onDeepLinkDealChange],
  )

  const openCreateDeal = useCallback(() => {
    setDealModal({
      ...CLOSED_DEAL_MODAL,
      open: true,
    })
    onDeepLinkDealChange?.(null)
  }, [onDeepLinkDealChange])

  const openEditDeal = useCallback(
    deal => {
      const dealId = deal?.dealId || null
      setDealModal({
        open: true,
        dealId,
        ownerNameHint: deal?.ownerName || '',
        primaryVenueNameHint: deal?.primaryVenueName || '',
        createDraft: null,
      })
      if (dealId) {
        onDeepLinkDealChange?.(dealId)
      }
    },
    [onDeepLinkDealChange],
  )

  const showAllDeals = useCallback(() => {
    applyStickyFilters({
      ownerFilter: CATCH_BOARD_OWNER_FILTER.ALL,
      readinessFilter: CATCH_BOARD_DEFAULT_READINESS_FILTER,
      search: '',
      hideClosed: false,
    })
  }, [applyStickyFilters])

  useEffect(() => {
    if (!createHandoff?.draft) {
      return undefined
    }

    const draft = createHandoff.draft
    setDealModal({
      open: true,
      dealId: null,
      ownerNameHint: '',
      primaryVenueNameHint: draft.linkedVenues?.[0]?.name || '',
      createDraft: draft,
    })
    onDeepLinkDealChange?.(null)
    onCreateHandoffConsumed?.()
    return undefined
  }, [createHandoff, onCreateHandoffConsumed, onDeepLinkDealChange])

  useEffect(() => {
    if (!deepLinkDealId || createHandoff?.draft) {
      return undefined
    }
    setDealModal(prev => {
      if (prev.open && prev.dealId === deepLinkDealId) {
        return prev
      }
      return {
        open: true,
        dealId: deepLinkDealId,
        ownerNameHint: '',
        primaryVenueNameHint: '',
        createDraft: null,
      }
    })
    return undefined
  }, [deepLinkDealId, createHandoff])

  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      refresh()
    }
    wasActiveRef.current = isActive
  }, [isActive, refresh])

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActive) {
        refresh()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isActive, refresh])

  useEffect(() => {
    if (!isActive) {
      setDealModal(prev => (prev.open ? { ...CLOSED_DEAL_MODAL } : prev))
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive) {
      return undefined
    }
    const onKeyDown = event => {
      if (dealModal.open) {
        return
      }
      const shortcut = getCatchBoardShortcut(event)
      if (shortcut === 'new-deal') {
        event.preventDefault()
        openCreateDeal()
      } else if (shortcut === 'search') {
        event.preventDefault()
        focusCatchBoardSearch()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, dealModal.open, openCreateDeal])

  const showInitialLoading = loading && !board.deals.length && !error
  const dealCount = board.totals?.dealCount ?? board.deals.length
  const showBoard = !showInitialLoading && !error && board.columns
  const emptyCopy = getCatchBoardEmptyCopy(filters)
  // Hide WoW while syncing so filter changes never flash the previous colour.
  const momentumForStrip = loading ? null : board.totals?.momentum

  return (
    <Panel data-testid='catch-board'>
      <IntroRow>
        <Intro>
          <Title>Catch Board</Title>
          <Body>
            Shared sales pipeline for The Bridge — cast leads, work the deal,
            land the catch. Drag cards to move stages or reorder.
          </Body>
          <ShortcutHint>Shortcuts: N new deal · / search</ShortcutHint>
        </Intro>
        <NewDealButton
          type='primary'
          onClick={openCreateDeal}
          title='New deal (N)'
          data-testid='catch-board-new-deal'
        >
          New deal
        </NewDealButton>
      </IntroRow>

      <CatchBoardFilters
        ownerFilter={filters.ownerFilter}
        readinessFilter={filters.readinessFilter}
        search={filters.search}
        hideClosed={filters.hideClosed}
        onOwnerFilterChange={ownerFilter =>
          applyStickyFilters(prev => ({ ...prev, ownerFilter }))
        }
        onReadinessFilterChange={readinessFilter =>
          applyStickyFilters(prev => ({ ...prev, readinessFilter }))
        }
        onSearchChange={search => setFilters(prev => ({ ...prev, search }))}
        onHideClosedChange={hideClosed =>
          applyStickyFilters(prev => ({ ...prev, hideClosed }))
        }
      />

      {error ? (
        <Alert
          type='error'
          showIcon
          message={error}
          action={
            <Button size='small' onClick={refresh} data-testid='catch-board-retry'>
              Retry
            </Button>
          }
          data-testid='catch-board-error'
        />
      ) : null}

      {showInitialLoading ? (
        <LoadingWrap data-testid='catch-board-loading'>
          <Spin />
          <LoadingText>Loading deals…</LoadingText>
        </LoadingWrap>
      ) : null}

      {showBoard ? (
        <>
          <StatusBar data-testid='catch-board-status' aria-live='polite'>
            <StatusPrimary>
              {dealCount === 0
                ? emptyCopy.title
                : `${dealCount} deal${dealCount === 1 ? '' : 's'}`}
              {dealCount > 0 ? (
                <CatchBoardMomentumStrip
                  momentum={momentumForStrip}
                  showDealCount
                />
              ) : null}
              {loading || isPersisting ? ' · Syncing…' : ''}
            </StatusPrimary>
            {dealCount > 0 ? (
              <StatusSecondary>
                <CatchBoardTipLabel
                  label={`Pipeline ${board.totals.dealValueLabel}`}
                  tip={CATCH_BOARD_TOOLTIPS.pipelineValue}
                  testId='catch-board-pipeline-tip'
                />
                <CatchBoardMomentumStrip
                  momentum={momentumForStrip}
                  showDealValue
                />
                {board.totals.potentialDealValuePence ? (
                  <>
                    {' · '}
                    <CatchBoardTipLabel
                      label={`Potential ${board.totals.potentialDealValueLabel}`}
                      tip={CATCH_BOARD_TOOLTIPS.potentialValue}
                      testId='catch-board-potential-tip'
                    />
                  </>
                ) : null}
              </StatusSecondary>
            ) : null}
          </StatusBar>

          {dealCount === 0 ? (
            <CatchBoardEmptyState
              search={filters.search}
              hideClosed={filters.hideClosed}
              ownerFilter={filters.ownerFilter}
              readinessFilter={filters.readinessFilter}
              onCreateDeal={openCreateDeal}
              onShowAllDeals={showAllDeals}
            />
          ) : null}

          <CatchBoardBoard
            columns={board.columns}
            columnTotalsByStage={board.columnTotalsByStage}
            sensors={sensors}
            activeDeal={activeDeal}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            onDealClick={openEditDeal}
          />
        </>
      ) : null}

      <CatchBoardDealModal
        visible={dealModal.open}
        dealId={dealModal.dealId}
        ownerNameHint={dealModal.ownerNameHint}
        primaryVenueNameHint={dealModal.primaryVenueNameHint}
        createDraft={dealModal.createDraft}
        onClose={() => closeDealModal()}
        onSaved={() => refresh()}
      />
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
  padding: ${({ theme }) => theme.spacing.md || '16px'} 0;
`

const IntroRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
`

const Intro = styled.div`
  max-width: 40rem;
`

const Title = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.sm || '8px'};
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Body = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  line-height: 1.5;
`

const ShortcutHint = styled.p`
  margin: ${({ theme }) => theme.spacing.sm || '8px'} 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || theme.colours.stormGrey || '#888'};
`

const NewDealButton = styled(Button)`
  flex: 0 0 auto;
`

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm || '8px'};
  min-height: 4rem;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const LoadingText = styled.span`
  font-size: 14px;
`

const StatusBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.sm || '8px'}
    ${({ theme }) => theme.spacing.md || '16px'};
  padding: ${({ theme }) => theme.spacing.sm || '8px'} 0;
  border-top: 1px solid
    ${({ theme }) => theme.colours.lightGrey || 'rgba(0, 0, 0, 0.06)'};
`

const StatusPrimary = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const StatusSecondary = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

export default CatchBoard
